import { useState, useEffect, useCallback, useRef } from "react";
import SectionNav from "./components/SectionNav";
import HeroSection from "./components/HeroSection";
import TrackRecordSection from "./components/TrackRecordSection";
import GarageSection from "./components/GarageSection";
import ProjectsSection from "./components/ProjectsSection";
import CertificationsSection from "./components/CertificationsSection";
import ContactSection from "./components/ContactSection";
import LinkedInIcon from "./components/LinkedInIcon";
import { LINKEDIN_URL } from "./data/portfolio";

const SECTIONS = ["Home", "Track record", "Garage", "Projects", "Certifications", "Radio"] as const;
const SCROLL_THROTTLE_MS = 650;

function App() {
  const [current, setCurrent] = useState(0);
  const lastScrollRef = useRef(0);

  const move = useCallback((delta: number) => {
    setCurrent((i) => Math.min(SECTIONS.length - 1, Math.max(0, i + delta)));
  }, []);

  useEffect(() => {
    // Passive listener, no preventDefault: a section whose content overflows
    // must still be scrollable, otherwise the bottom is unreachable.
    const onWheel = (e: WheelEvent) => {
      const now = Date.now();
      if (now - lastScrollRef.current < SCROLL_THROTTLE_MS) return;
      if (Math.abs(e.deltaY) < 8) return;
      lastScrollRef.current = now;
      move(e.deltaY > 0 ? 1 : -1);
    };

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "ArrowDown" || e.key === "PageDown") move(1);
      else if (e.key === "ArrowUp" || e.key === "PageUp") move(-1);
    };

    window.addEventListener("wheel", onWheel, { passive: true });
    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("wheel", onWheel);
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [move]);

  const isHome = current === 0;

  return (
    <div className="relative h-screen min-h-[780px] w-full overflow-hidden bg-ground text-ink">
      <video
        autoPlay
        loop
        muted
        playsInline
        className="pointer-events-none absolute inset-0 z-0 h-full w-full object-cover
                   transition-[opacity,filter] duration-500"
        style={{
          opacity: isHome ? 0.5 : 0.16,
          filter: isHome ? "saturate(.65) contrast(1.05)" : "saturate(.35) blur(3px)"
        }}
      >
        <source src="/F1-car-racing-v4.mp4" type="video/mp4" />
      </video>

      {/* Nocturne ground pulled back over the footage so type stays legible. */}
      <div className="pointer-events-none absolute inset-0 z-[1] bg-[linear-gradient(100deg,#161826_0%,rgba(22,24,38,.94)_34%,rgba(22,24,38,.62)_62%,rgba(22,24,38,.88)_100%)]" />
      <div className="pointer-events-none absolute inset-0 z-[1] bg-[radial-gradient(120%_90%_at_8%_100%,rgba(60,52,110,.38)_0%,transparent_60%)]" />

      <header className="absolute inset-x-0 top-0 z-30 flex items-center justify-between
                         border-b border-white/[0.16] px-11 py-5 backdrop-blur-md
                         bg-gradient-to-b from-ground/70 to-ground/30">
        <button
          type="button"
          onClick={() => setCurrent(0)}
          className="flex select-none items-center gap-3"
        >
          <span className="grid h-[30px] w-[30px] place-items-center rounded-sm border
                           border-accent text-xs font-semibold tracking-[0.06em]
                           text-accent shadow-glow">
            MD
          </span>
          <span className="font-heading text-sm font-medium uppercase tracking-[0.22em]">
            Race Team
          </span>
        </button>

        <div className="flex items-center gap-[22px]">
          <span className="text-[10.5px] uppercase tracking-[0.2em] tabular-nums text-neutral-500">
            Lap {String(current + 1).padStart(2, "0")} / {String(SECTIONS.length).padStart(2, "0")} · {SECTIONS[current]}
          </span>
          <a
            href={LINKEDIN_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 rounded-md border border-accent
                       px-3 py-1.5 text-xs font-medium text-accent-300
                       transition-colors duration-200 hover:bg-accent-900/40"
          >
            <LinkedInIcon size={13} />
            LinkedIn
          </a>
        </div>
      </header>

      <SectionNav sections={SECTIONS} current={current} onSelect={setCurrent} />

      <main className="relative z-20 flex h-full flex-col justify-center overflow-y-auto
                       py-24 pl-[250px] pr-[60px]">
        {current === 0 && <HeroSection onNavigate={setCurrent} />}
        {current === 1 && <TrackRecordSection />}
        {current === 2 && <GarageSection />}
        {current === 3 && <ProjectsSection />}
        {current === 4 && <CertificationsSection />}
        {current === 5 && <ContactSection />}
      </main>

      <div
        className="absolute bottom-0 left-0 z-30 h-0.5 bg-gradient-to-r from-accent-700
                   to-accent shadow-rail transition-[width] duration-500"
        style={{ width: `${((current + 1) / SECTIONS.length) * 100}%` }}
      />

      <p className="absolute bottom-[22px] right-11 z-30 text-[10.5px] uppercase
                    tracking-[0.18em] text-neutral-600">
        Scroll or ↑ ↓ to change sector
      </p>
    </div>
  );
}

export default App;
